import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import {
  getAccountExpirationDate,
  getCurrentAccount,
  hasAccountOnlyDemoPlan,
} from '../../lib/store/crm/helpers';

import StockLogos from '../../assets/img/stock_logos.png';
import { useTimeRemaining } from '../../hooks/useTimeRemaining';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import useIsMobile from '../../shared_components/hooks/useIsMobile';
import { UI_ROUTES } from '../../util/routes';
import DemoAccountActivationPopup from '../DemoAccountActivationPopup/DemoAccountActivationPopup';

import { getDescription, getWelcomeTitle } from './NotVerifiedNotification.helpers';

import styles from './NotVerifiedNotification.module.scss';

// Whether this notification is the one to show is decided by `NotificationsManager`.
const NotVerifiedNotification = (props: ReduxProps) => {
  const {
    expirationDate: endDate,
    extendedInfo,
    hasOnlyDemoPlan,
    currentAccount,
  } = props;
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const [isDisplayDemoActivationModal, setIsDisplayDemoActivationModal] = useState(false);
  const { hasNoRemainingTime } = useTimeRemaining(endDate);
  const needToShowDemoButton = !currentAccount;

  const firstNameOrEmptyString = extendedInfo?.first_name ?? '';

  const welcomeTitle = getWelcomeTitle({
    firstName: firstNameOrEmptyString,
    hasOnlyDemoPlan,
    hasNoRemainingTime,
  });

  const description = getDescription({ hasNoRemainingTime });

  if (isMobile) {
    return (
      <div className={styles.container}>
        <p className={styles.title}>{welcomeTitle}</p>
        <p className={styles.description}>{description}</p>
        <div className={styles.buttons}>
          <Button
            fullWidth
            className={classNames(styles.planButton, styles.choosePlan)}
            variant={ButtonTypesEnum.Primary}
            onClick={() => {
              navigate(UI_ROUTES.products);
            }}
          >
            {lang.commonSelectPlan()}
          </Button>
          {needToShowDemoButton && (
            <Button
              fullWidth
              className={styles.demoButton}
              variant={ButtonTypesEnum.Tertiary}
              onClick={() => {
                setIsDisplayDemoActivationModal(true);
              }}
            >
              {lang.tryADemo()}
            </Button>
          )}
        </div>
        <DemoAccountActivationPopup
          isDisplayModal={isDisplayDemoActivationModal}
          setIsDisplayModal={setIsDisplayDemoActivationModal}
        />
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.containerWeb]: !isMobile,
      })}
    >
      <div className={styles.content}>
        <div className={styles.titleAndButtons}>
          <div className={styles.titleAndDesc}>
            <p className={styles.title}>{welcomeTitle}</p>
            <p className={styles.description}>{description}</p>
          </div>
              {needToShowDemoButton && (
            <div className={styles.buttons}>
              <Button
                className={styles.planButton}
                variant={ButtonTypesEnum.Primary}
                onClick={() => {
                  navigate(UI_ROUTES.products);
                }}
              >
                {lang.commonSelectPlan()}
              </Button>
              <Button
                className={styles.demoButton}
                variant={ButtonTypesEnum.Tertiary}
                onClick={() => {
                  setIsDisplayDemoActivationModal(true);
                }}
              >
                {lang.tryADemo()}
              </Button>
          </div>
          )}
          <DemoAccountActivationPopup
            isDisplayModal={isDisplayDemoActivationModal}
            setIsDisplayModal={setIsDisplayDemoActivationModal}
          />
        </div>
        {!needToShowDemoButton ? (
          <div className={styles.onlyDemoPlanButtonWrapper}>
            <Button
              className={styles.planButton}
              variant={ButtonTypesEnum.Primary}
              onClick={() => {
                navigate(UI_ROUTES.products);
              }}
            >
              {lang.commonSelectPlan()}
            </Button>
          </div>
        ) : (
          <img src={StockLogos} alt="" className={styles.stockLogos} />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentAccount: getCurrentAccount(state),
  extendedInfo: state.crm.individualExtendedInfo,
  hasOnlyDemoPlan: hasAccountOnlyDemoPlan(state.crm),
  expirationDate: getAccountExpirationDate(state),
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(NotVerifiedNotification);
